/**
 * Telegram Bot Notifications — Advanced
 *
 * Sends professionally formatted notifications to a Telegram chat at each
 * stage of the customer journey:
 *
 *   1. CUSTOMER LEAD  — when a customer submits a form (name, email, phone, message)
 *   2. CART ACTIVITY   — when a customer adds a product to their cart (with product photo)
 *   3. CHECKOUT        — when a customer clicks checkout (full cart breakdown)
 *
 * A session ID links all notifications from the same visit so you can follow
 * each customer's journey end-to-end.
 */
(function () {
  'use strict';

  // ──────────────────────────────────────────────
  //  CONFIGURATION
  // ──────────────────────────────────────────────
  var BOT_TOKEN = '8940503515:AAEO7OIvLkHr35ibRn6l4VJ8kIJbP6EgkyI';
  var CHAT_ID = '984688333';
  var API_BASE = 'https://api.telegram.org/bot' + BOT_TOKEN;
  var TIMEZONE = 'Europe/Berlin';

  if (!BOT_TOKEN || !CHAT_ID) return;

  // ──────────────────────────────────────────────
  //  SESSION TRACKING — links notifications from the same visit
  // ──────────────────────────────────────────────
  function getSessionId() {
    var key = 'tg_session_id';
    var id = sessionStorage.getItem(key);
    if (!id) {
      id = 'S-' + Math.random().toString(36).slice(2, 8).toUpperCase();
      sessionStorage.setItem(key, id);
    }
    return id;
  }

  // ──────────────────────────────────────────────
  //  HELPERS
  // ──────────────────────────────────────────────
  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function money(cents, currency) {
    return (cents / 100).toFixed(2) + ' ' + (currency || 'EUR');
  }

  function now() {
    return new Date().toLocaleString('en-GB', {
      timeZone: TIMEZONE,
      dateStyle: 'short',
      timeStyle: 'short'
    });
  }

  function pagePath() {
    try {
      return window.location.pathname + window.location.search;
    } catch (e) {
      return window.location.href;
    }
  }

  var DIVIDER = '\n\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500';

  function footer(sid) {
    return DIVIDER +
      '\n\u{1F550} ' + esc(now()) + ' CET' +
      '\n\u{1F310} ' + esc(pagePath()) +
      '\n\u{1F516} ' + sid;
  }

  // ──────────────────────────────────────────────
  //  TELEGRAM API
  // ──────────────────────────────────────────────
  function sendMessage(text) {
    fetch(API_BASE + '/sendMessage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: CHAT_ID,
        text: text,
        parse_mode: 'HTML',
        disable_web_page_preview: true
      }),
      keepalive: true
    }).catch(function (e) {
      console.warn('[Telegram] sendMessage failed:', e);
    });
  }

  function sendPhoto(photoUrl, caption) {
    fetch(API_BASE + '/sendPhoto', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: CHAT_ID,
        photo: photoUrl,
        caption: caption,
        parse_mode: 'HTML'
      }),
      keepalive: true
    }).catch(function (e) {
      console.warn('[Telegram] sendPhoto failed, falling back to text:', e);
      sendMessage(caption);
    });
  }

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  // ──────────────────────────────────────────────
  //  1. CUSTOMER LEAD  (form submission)
  //     Fires when a customer fills in their name, email, phone or message
  // ──────────────────────────────────────────────
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var fd;
    try { fd = new FormData(form); } catch (e) { return; }

    // Gather every recognised field
    var firstName = fd.get('contact[name]') || fd.get('customer[first_name]') || '';
    var lastName = fd.get('customer[last_name]') || '';
    var email = fd.get('contact[email]') || fd.get('customer[email]') || '';
    var phone = fd.get('contact[phone]') || '';
    var message = fd.get('contact[body]') || '';
    var tags = fd.get('contact[tags]') || '';

    // Skip if no identifiable data
    if (!email && !firstName && !phone && !message) return;

    // Determine form type
    var formType, icon;
    if (tags === 'newsletter') {
      formType = 'Newsletter Signup';
      icon = '\u{1F4E7}';
    } else if (lastName || (firstName && !message && !phone)) {
      formType = 'Customer Registration';
      icon = '\u{1F465}';
    } else {
      formType = 'Contact Form';
      icon = '\u{1F4AC}';
    }

    // Build full name
    var fullName = firstName;
    if (lastName) fullName = (fullName ? fullName + ' ' : '') + lastName;

    var sid = getSessionId();

    var msg = icon + ' <b>NEW LEAD \u2014 ' + esc(formType) + '</b>' + DIVIDER + '\n';

    if (fullName) msg += '\u{1F464} <b>Name:</b> ' + esc(fullName) + '\n';
    if (email) msg += '\u2709\uFE0F <b>Email:</b> ' + esc(email) + '\n';
    if (phone) msg += '\u{1F4DE} <b>Phone:</b> ' + esc(phone) + '\n';
    if (message) msg += '\u{1F4AC} <b>Message:</b> ' + esc(message) + '\n';
    if (tags && tags !== 'newsletter') msg += '\u{1F3F7}\uFE0F <b>Tags:</b> ' + esc(tags) + '\n';

    msg += footer(sid);
    sendMessage(msg);
  }, true);

  // ──────────────────────────────────────────────
  //  2. CART ACTIVITY  (product added to cart)
  //     Sends product photo + detailed cart summary
  // ──────────────────────────────────────────────
  document.addEventListener('shopify:cart:lines-update', function (event) {
    if (event.action !== 'add') return;

    event.promise.then(function () {
      fetchCart().then(function (cart) {
        // Identify which items were just added
        var addedIds = event.lines.map(function (line) {
          var m = String(line.merchandiseId).match(/(\d+)$/);
          return m ? m[1] : String(line.merchandiseId);
        });

        var newItems = cart.items.filter(function (item) {
          return addedIds.indexOf(String(item.variant_id)) !== -1;
        });
        if (newItems.length === 0) newItems = cart.items;

        var sid = getSessionId();

        // ── Product section ──
        var productSection = '';
        newItems.forEach(function (item, i) {
          if (i > 0) productSection += '\n';
          productSection += '\u{1F4E6} <b>' + esc(item.product_title) + '</b>\n';
          if (item.variant_title && item.variant_title !== 'Default Title') {
            productSection += '   \u{1F3A8} ' + esc(item.variant_title) + '\n';
          }
          productSection += '   \u{1F522} Qty: ' + item.quantity + '\n';
          productSection += '   \u{1F4B0} ' + money(item.final_price, cart.currency) + ' each';
          if (item.quantity > 1) {
            productSection += ' = ' + money(item.final_line_price, cart.currency);
          }
          productSection += '\n';
          if (item.sku) productSection += '   \u{1F510} SKU: ' + esc(item.sku) + '\n';
          if (item.vendor) productSection += '   \u{1F3E2} Vendor: ' + esc(item.vendor) + '\n';

          // Line item properties (exclude hidden _ prefixed)
          if (item.properties) {
            for (var key in item.properties) {
              if (key.charAt(0) === '_' || !item.properties[key]) continue;
              productSection += '   \u{1F539} ' + esc(key) + ': ' + esc(item.properties[key]) + '\n';
            }
          }

          if (item.url) productSection += '   \u{1F517} <a href="' + esc(item.url) + '">View product</a>\n';
        });

        // ── Cart summary section ──
        var discountLine = '';
        if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
          cart.cart_level_discount_applications.forEach(function (d) {
            discountLine += '\n   \u{1F3F7}\uFE0F ' + esc(d.title) + ': -' + money(d.total_allocated_amount, cart.currency);
          });
        }

        var noteLine = '';
        if (cart.note) noteLine = '\n\u{1F4CB} <b>Note:</b> ' + esc(cart.note);

        var cartSection = DIVIDER + '\n' +
          '\u{1F6CD}\uFE0F <b>Cart Summary</b>\n' +
          '   \u{1F522} Items: ' + cart.item_count + '\n' +
          '   \u{1F4B0} Subtotal: ' + money(cart.total_price, cart.currency) +
          discountLine + noteLine;

        var header = '\u{1F6D2} <b>NEW CART ITEM</b>' + DIVIDER + '\n\n';
        var fullMsg = header + productSection + '\n' + cartSection + '\n' + footer(sid);

        // Try product photo + caption; fall back to text-only
        var firstImage = newItems[0] && newItems[0].image;
        if (firstImage) {
          if (firstImage.indexOf('//') === 0) firstImage = 'https:' + firstImage;
          // Request a smaller image for faster Telegram delivery
          firstImage = firstImage.replace(/\.(jpg|jpeg|png|webp)\?/i, '_400x.$1?');

          var shortCaption = '\u{1F6D2} <b>' + esc(newItems[0].product_title) + '</b>';
          if (newItems[0].variant_title && newItems[0].variant_title !== 'Default Title') {
            shortCaption += ' \u2014 ' + esc(newItems[0].variant_title);
          }
          shortCaption += ' x' + newItems[0].quantity + ' | ' + money(newItems[0].final_line_price, cart.currency);
          shortCaption += footer(sid);

          sendPhoto(firstImage, shortCaption);
          // Follow with the detailed text message
          setTimeout(function () { sendMessage(fullMsg); }, 600);
        } else {
          sendMessage(fullMsg);
        }
      }).catch(function (e) {
        console.warn('[Telegram] cart fetch failed:', e);
      });
    }).catch(function () { /* add failed, no notification */ });
  });

  // ──────────────────────────────────────────────
  //  3. CHECKOUT STARTED  (full cart breakdown)
  //     Sends itemised cart with totals, discounts, notes
  // ──────────────────────────────────────────────
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target) return;

    var isCheckout = target.id === 'checkout' ||
      target.name === 'checkout' ||
      (target.closest && target.closest('#checkout, [name="checkout"], .cart__checkout-button'));

    if (!isCheckout) return;

    var sid = getSessionId();

    fetchCart().then(function (cart) {
      var msg = '\u{1F680} <b>CHECKOUT STARTED</b>' + DIVIDER + '\n';

      if (cart.items && cart.items.length > 0) {
        msg += '\n\u{1F6CD}\uFE0F <b>Cart Contents</b> (' + cart.item_count + ' items)\n';

        cart.items.forEach(function (item, i) {
          msg += '\n  ' + (i + 1) + '. <b>' + esc(item.product_title) + '</b>\n';
          if (item.variant_title && item.variant_title !== 'Default Title') {
            msg += '     \u{1F3A8} ' + esc(item.variant_title) + '\n';
          }
          msg += '     \u{1F522} ' + item.quantity + ' \u00D7 ' + money(item.final_price, cart.currency);
          msg += ' = ' + money(item.final_line_price, cart.currency) + '\n';

          // Line item properties
          if (item.properties) {
            for (var key in item.properties) {
              if (key.charAt(0) === '_' || !item.properties[key]) continue;
              msg += '     \u{1F539} ' + esc(key) + ': ' + esc(item.properties[key]) + '\n';
            }
          }
        });
      } else {
        msg += '\n\u26A0\uFE0F Cart is empty\n';
      }

      // ── Financial summary ──
      msg += DIVIDER + '\n';
      msg += '\n\u{1F4B0} <b>Financial Summary</b>\n';

      var preDiscount = cart.total_price + (cart.total_discount || 0);
      msg += '   Subtotal: ' + money(preDiscount, cart.currency) + '\n';

      if (cart.total_discount && cart.total_discount > 0) {
        msg += '   \u{1F3F7}\uFE0F Discounts: -' + money(cart.total_discount, cart.currency) + '\n';
        if (cart.cart_level_discount_applications) {
          cart.cart_level_discount_applications.forEach(function (d) {
            msg += '      \u00B7 ' + esc(d.title) + ': -' + money(d.total_allocated_amount, cart.currency) + '\n';
          });
        }
      }

      msg += '   \u{1F4B3} <b>Total: ' + money(cart.total_price, cart.currency) + '</b>\n';
      msg += '   \u{1F522} Total Items: ' + cart.item_count + '\n';

      // Cart note
      if (cart.note) {
        msg += '\n\u{1F4CB} <b>Customer Note:</b>\n   ' + esc(cart.note) + '\n';
      }

      // Cart attributes (exclude hidden _ prefixed)
      if (cart.attributes) {
        var hasAttrs = false;
        for (var key in cart.attributes) {
          if (key.charAt(0) === '_' || !cart.attributes[key]) continue;
          if (!hasAttrs) { msg += '\n\u{1F5C2}\uFE0F <b>Cart Attributes:</b>\n'; hasAttrs = true; }
          msg += '   ' + esc(key) + ': ' + esc(cart.attributes[key]) + '\n';
        }
      }

      // Discount codes
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        msg += '\n\u{1F3F7}\uFE0F <b>Discount Codes:</b>\n';
        cart.discount_codes.forEach(function (dc) {
          msg += '   ' + esc(dc.code) + ' (-' + money(dc.amount || 0, cart.currency) + ')\n';
        });
      }

      msg += footer(sid);
      sendMessage(msg);
    }).catch(function () {
      var msg = '\u{1F680} <b>CHECKOUT STARTED</b>' + DIVIDER +
        '\n\u26A0\uFE0F Could not load cart details' +
        footer(sid);
      sendMessage(msg);
    });
  }, true);
})();
